




import React, { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../Common/Configurations/APIConfig';
import DefaultData from '../Common/CompanyData/DefaultData'; 
import './BusinessDashboard.css';

function BusinessDashboard() {
  const [salonId, setSalonId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentMonth, setCurrentMonth] = useState('');
  
  const [dashboard, setDashboard] = useState({
    todayAppointmentsList: [],
    summary: {
      totalAppointments: 0,
      todayAppointments: 0,
      totalRevenue: 0,
      completedThisMonth: 0,
      totalStaff: 0,
      pendingAppointments: 0,
      upcomingAppointments: 0,
      confirmedAppointments: 0,
      cancelledAppointments: 0
    }
  });

  const currency = DefaultData.DEFAULTCURRENCY || 'NGN';

  useEffect(() => {
    const authData = getAuthDataFromStorage();
    if (authData && authData.salonId) {
      setSalonId(authData.salonId);
      setCurrentMonth(new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }));
    } else {
      setError('Please login to access dashboard');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (salonId) {
      fetchAllDashboardData();
    }
  }, [salonId]);

  const getAuthDataFromStorage = () => {
    try {
      const authString = localStorage.getItem('auth');
      if (authString) return JSON.parse(authString);
      const sId = localStorage.getItem('salonId');
      if (sId) {
        return { salonId: sId, roleName: localStorage.getItem('roleName') };
      }
      return null;
    } catch (error) {
      console.error('Error parsing auth data:', error);
      return null;
    }
  };

  const fetchAllDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const dashboardRes = await axios.get(
        `${config.APIACTIVEURL}Dashboard/salon/${salonId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      const today = new Date();
      const startDate = new Date(today.getFullYear(), today.getMonth(), 1);
      const endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      
      const revenueRes = await axios.get(
        `${config.APIACTIVEURL}Dashboard/salon/${salonId}/revenue`,
        {
          params: {
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0]
          },
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (dashboardRes.data.success && revenueRes.data.success) {
        const dashboardData = dashboardRes.data.data;
        const revenueData = revenueRes.data.data;
        setDashboard({
          todayAppointmentsList: dashboardData.todayAppointmentsList || [],
          summary: {
            totalAppointments: dashboardData.totalAppointments || 0,
            todayAppointments: dashboardData.todayAppointments || 0,
            totalRevenue: revenueData.totalRevenue || 0,
            completedThisMonth: revenueData.totalCompletedAppointments || 0,
            totalStaff: dashboardData.totalStaff || 0,
            pendingAppointments: dashboardData.pendingAppointments || 0,
            upcomingAppointments: dashboardData.upcomingAppointments || 0,
            confirmedAppointments: dashboardData.confirmedAppointments || 0,
            cancelledAppointments: dashboardData.cancelledAppointments || 0
          }
        });
      }
    } catch (error) {
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getAppointmentStatus = (status) => {
    const statusConfig = {
      'pending': { color: '#FF9800', label: 'Pending' },
      'confirmed': { color: '#4CAF50', label: 'Confirmed' },
      'completed': { color: '#2196F3', label: 'Completed' },
      'cancelled': { color: '#F44336', label: 'Cancelled' }
    };
    const config = statusConfig[status?.toLowerCase()] || { color: '#9E9E9E', label: status };
    return { color: config.color, label: config.label, bgColor: `${config.color}15` };
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getAverageRevenue = () => {
    if (dashboard.summary.completedThisMonth > 0) {
      return (dashboard.summary.totalRevenue / dashboard.summary.completedThisMonth).toFixed(2);
    }
    return '0.00';
  };

  const formatCurrency = (amount) => `${currency} ${amount || '0.00'}`;

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <div className="error-icon">⚠️</div>
        <h3>Unable to Load Dashboard</h3>
        <p>{error}</p>
        <button className="retry-btn" onClick={fetchAllDashboardData}>Try Again</button>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-content">
          <h1><span className="header-icon">📊</span> Salon Dashboard</h1>
          <p className="header-subtitle">{currentMonth} Overview</p>
        </div>
        <div className="header-date">
          <span className="date-icon">📅</span>
          {new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="metrics-grid">
        <div className="metric-card revenue-card">
          <div className="metric-icon">📋</div>
          <div className="metric-content">
            <div className="metric-value">{dashboard.summary.totalAppointments}</div>
            <div className="metric-label">Total Appointments</div>
          </div>
        </div>
        <div className="metric-card appointment-card">
          <div className="metric-icon">📅</div>
          <div className="metric-content">
            <div className="metric-value">{dashboard.summary.todayAppointments}</div>
            <div className="metric-label">Today's Appointments</div>
          </div>
        </div>
        <div className="metric-card completed-card">
          <div className="metric-icon">✅</div>
          <div className="metric-content">
            <div className="metric-value">{dashboard.summary.completedThisMonth}</div>
            <div className="metric-label">Completed This Month</div>
          </div>
        </div>
        <div className="metric-card staff-card">
          <div className="metric-icon">👥</div>
          <div className="metric-content">
            <div className="metric-value">{dashboard.summary.totalStaff}</div>
            <div className="metric-label">Total Staff</div>
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-icon">⏳</div>
          <div className="metric-content">
            <div className="metric-value">{dashboard.summary.pendingAppointments}</div>
            <div className="metric-label">Pending</div>
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-icon">✔️</div>
          <div className="metric-content">
            <div className="metric-value">{dashboard.summary.confirmedAppointments}</div>
            <div className="metric-label">Confirmed</div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="dashboard-content">
        {/* Today's Schedule */}
        <div className="content-card appointments-section">
          <div className="section-header">
            <h2>
              <span className="section-icon">🎯</span> Today's Schedule
              <span className="section-count">{dashboard.summary.todayAppointments}</span>
            </h2>
          </div>

          <div className="appointments-table-container">
            {/* Desktop View Table */}
            <div className="appointments-table d-none d-lg-flex" style={{ flexDirection: 'column', width: '100%' }}>
              <div className="table-header" style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb', padding: '0 24px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '100px 2fr 3fr 120px', alignItems: 'center', height: '60px', gap: '20px' }}>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', display: 'flex', alignItems: 'center', margin: 0 }}>Time</div>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', display: 'flex', alignItems: 'center', margin: 0 }}>Customer</div>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', display: 'flex', alignItems: 'center', margin: 0 }}>Service & Staff</div>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', display: 'flex', alignItems: 'center', margin: 0 }}>Status</div>
                </div>
              </div>
              <div className="table-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {dashboard.todayAppointmentsList.length > 0 ? (
                  dashboard.todayAppointmentsList.map((appointment, index) => {
                    const status = getAppointmentStatus(appointment.status);
                    return (
                      <div key={appointment.appointmentId || index} className="table-row" style={{ display: 'grid', gridTemplateColumns: '100px 2fr 3fr 120px', alignItems: 'center', padding: '16px 24px', gap: '20px', borderBottom: '1px solid #f3f4f6' }}>
                        <div className="table-cell time-cell" style={{ margin: 0 }}>{formatTime(appointment.startTime)}</div>
                        <div className="table-cell customer-cell" style={{ margin: 0 }}>
                          <div className="customer-info">
                            <div className="customer-name">{appointment.customerName || 'Customer'}</div>
                            <div className="customer-phone">{appointment.customerPhone || 'N/A'}</div>
                          </div>
                        </div>
                        <div className="table-cell service-cell" style={{ margin: 0 }}>
                          <div className="service-details">
                            <div className="service-name">{appointment.serviceName}</div>
                            <div className="staff-name">{appointment.staffName}</div>
                          </div>
                        </div>
                        <div className="table-cell status-cell" style={{ margin: 0 }}>
                          <span className="status-text" style={{ color: status.color, fontWeight: '600' }}>{status.label}</span>
                        </div>
                      </div>
                    );
                  })
                ) : <div className="p-5 text-center text-muted">No appointments today</div>}
              </div>
            </div>

            {/* Mobile View Cards */}
            <div className="d-lg-none p-3">
               {dashboard.todayAppointmentsList.length > 0 ? (
                  dashboard.todayAppointmentsList.map((appointment, index) => {
                    const status = getAppointmentStatus(appointment.status);
                    return (
                      <div key={index} className="mobile-dash-card mb-3 p-3 border rounded-3 bg-white shadow-sm">
                        <div className="d-flex justify-content-between mb-2">
                           <span className="fw-bold text-primary">{formatTime(appointment.startTime)}</span>
                           <span className="small fw-bold" style={{ color: status.color }}>{status.label}</span>
                        </div>
                        <h6 className="fw-bold mb-1">{appointment.customerName}</h6>
                        <div className="small text-muted mb-2">{appointment.serviceName}</div>
                        <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                           <small><i className="ri-user-line me-1"></i> {appointment.staffName}</small>
                           <small className="text-muted">{appointment.customerPhone}</small>
                        </div>
                      </div>
                    );
                  })
               ) : <div className="text-center py-4 text-muted">No appointments scheduled</div>}
            </div>
          </div>
        </div>

        {/* Monthly Summary */}
        <div className="content-card summary-section">
          <div className="section-header">
            <h2><span className="section-icon">📈</span> Performance <span className="section-month">{currentMonth}</span></h2>
          </div>
          <div className="summary-stats">
            <div className="summary-stat">
              <div className="stat-icon revenue-icon">💰</div>
              <div className="stat-content">
                <div className="stat-label">Total Revenue</div>
                <div className="stat-value">{formatCurrency(dashboard.summary.totalRevenue)}</div>
                <div className="stat-detail">From {dashboard.summary.completedThisMonth} appointments</div>
              </div>
            </div>
            <div className="summary-stat">
              <div className="stat-icon appointment-icon">📅</div>
              <div className="stat-content">
                <div className="stat-label">Avg. Revenue</div>
                <div className="stat-value">{formatCurrency(getAverageRevenue())}</div>
                <div className="stat-detail">Per completed booking</div>
              </div>
            </div>
          </div>
          <div className="summary-footer">
            <div className="footer-note d-none d-sm-block">Real-time data synchronization</div>
            <button className="refresh-btn" onClick={fetchAllDashboardData}>
              <span className="refresh-icon">🔄</span> Refresh
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BusinessDashboard;